import { call, put, select, takeLatest } from 'redux-saga/effects';
import i18n from '../../../i18n';
import {
  fetchIdentityTypes,
  fetchIdentityTypesSuccess,
  fetchIdentityTypesFailure,
  fetchIdentityTypeById,
  fetchIdentityTypeByIdSuccess,
  fetchIdentityTypeByIdFailure,
  addIdentityType,
  addIdentityTypeSuccess,
  addIdentityTypeFailure,
  updateIdentityType,
  updateIdentityTypeSuccess,
  updateIdentityTypeFailure,
  deleteIdentityType,
  deleteIdentityTypeSuccess,
  deleteIdentityTypeFailure,
  bulkDeleteIdentityTypes,
  bulkDeleteIdentityTypesSuccess,
  bulkDeleteIdentityTypesFailure
} from './slice';
import * as identityTypeService from '../../../services/identityTypeService';

// Selector to get current state
const getIdentityTypesState = (state) => state.identityTypes;

// Fetch identity types saga
function* fetchIdentityTypesSaga(action) {
  try {
    const { page, pageSize, sortColumn, sortDirection, filterName, filterIsActive } = action.payload || {};

    // Normalize like Vehicles page
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      // Convert to PascalCase expected by backend (e.g., id -> Id, name -> Name)
      const pascalSort = String(sortColumn).replace(/^[a-z]/, (m) => m.toUpperCase());
      params.sortColumn = pascalSort;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterName) {
      params.search = filterName;
      params.searchTerm = filterName; // support API expecting searchTerm
    }
    if (filterIsActive !== undefined && filterIsActive !== null) {
      const statuses = Array.isArray(filterIsActive)
        ? filterIsActive
        : (filterIsActive === '' ? [] : [filterIsActive]);
      const normalized = statuses.map((s) => (s === true || s === 'true' || s === 1 || s === '1'));
      const unique = Array.from(new Set(normalized));
      if (unique.length === 1) {
        params.isActive = unique[0];
      }
      // if both selected or none, omit filter
    }

    const response = yield call(identityTypeService.getAllIdentityTypes, params);
    yield put(fetchIdentityTypesSuccess(response));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(fetchIdentityTypesFailure(errorMessage));
  }
}

// Fetch identity type by ID saga
function* fetchIdentityTypeByIdSaga(action) {
  try {
    const response = yield call(identityTypeService.getIdentityTypeById, action.payload);
    yield put(fetchIdentityTypeByIdSuccess(response));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(fetchIdentityTypeByIdFailure(errorMessage));
  }
}

// Add identity type saga
function* addIdentityTypeSaga(action) {
  try {
    const response = yield call(identityTypeService.createIdentityType, action.payload);
    yield put(addIdentityTypeSuccess({
      data: response,
      message: response.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(addIdentityTypeFailure(errorMessage));
  }
}

// Update identity type saga
function* updateIdentityTypeSaga(action) {
  try {
    const { id, ...data } = action.payload;
    const response = yield call(identityTypeService.updateIdentityType, id, data);
    yield put(updateIdentityTypeSuccess({
      data: response,
      message: response.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(updateIdentityTypeFailure(errorMessage));
  }
}

// Delete identity type saga
function* deleteIdentityTypeSaga(action) {
  try {
    const response = yield call(identityTypeService.deleteIdentityType, action.payload);
    yield put(deleteIdentityTypeSuccess({
      message: response?.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(deleteIdentityTypeFailure(errorMessage));
  }
}

// Bulk delete identity types saga
function* bulkDeleteIdentityTypesSaga(action) {
  try {
    const response = yield call(identityTypeService.bulkDeleteIdentityTypes, action.payload);
    yield put(bulkDeleteIdentityTypesSuccess({
      message: response?.message
    }));
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    yield put(bulkDeleteIdentityTypesFailure(errorMessage));
  }
}

// Watcher saga
export default function* identityTypesSaga() {
  yield takeLatest(fetchIdentityTypes.type, fetchIdentityTypesSaga);
  yield takeLatest(fetchIdentityTypeById.type, fetchIdentityTypeByIdSaga);
  yield takeLatest(addIdentityType.type, addIdentityTypeSaga);
  yield takeLatest(updateIdentityType.type, updateIdentityTypeSaga);
  yield takeLatest(deleteIdentityType.type, deleteIdentityTypeSaga);
  yield takeLatest(bulkDeleteIdentityTypes.type, bulkDeleteIdentityTypesSaga);
}
